import { useEffect } from "react";
import Layout from '../utils/core/Layout';
import './CookiesPolicy.css';  // Same stylesheet used for Cookies & Terms pages

const PrivacyPolicyPage = () => {
  useEffect(() => {
    // scroll only when the page URL contains #top
    if (window.location.hash === "#top") {
      const heading = document.querySelector(".policy-page");
      if (heading) {
        heading.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, []);

  return (
    <Layout>
      <div className="policy-page">
        <h1 className="policy-title">Privacy Policy</h1>

        {/* Blue banner */}
        <div className="policy-banner">
          <p className="policy-banner-updated">
            <strong>Last Updated:</strong> 27/11/2025
          </p>
          <p className="policy-banner-text">
            PLEASE READ THIS PRIVACY POLICY CAREFULLY TO UNDERSTAND HOW WE HANDLE YOUR DATA.
          </p>
        </div>

        {/* 1. Overview */}
        <section className="policy-section">
          <h2>1. Overview</h2>
          <p>
            This Privacy Policy applies to the goneet website, Android app, and iOS app
            (collectively, the “Platform”).
          </p>
          <p>
            This Privacy Policy explains how goneet (“we”, “our”, “us”) collects, uses, stores, and
            protects your personal information.
          </p>
          <p>
            We do not collect age, date of birth, gender, or any sensitive personal data.
          </p>
          <p>
            By using the Platform, you agree to this Privacy Policy.
          </p>
        </section>

        {/* 2. Information We Collect */}
        <section className="policy-section">
          <h2>2. Information We Collect</h2>
          <p>We collect the following categories of information:</p>

          <h3>A. Account Information</h3>
          <ul className="policy-list">
            <li className="policy-list-item">Name</li>
            <li className="policy-list-item">Email address</li>
            <li className="policy-list-item">Password</li>
            <li className="policy-list-item">Country / Location</li>
          </ul>

          <h3>B. Professional Profile Information</h3>
          <p>You voluntarily provide this as part of building your goneet professional profile:</p>
          <ul className="policy-list">
            <li className="policy-list-item">Industry sectors</li>
            <li className="policy-list-item">Years of experience</li>
            <li className="policy-list-item">Sector phases</li>
            <li className="policy-list-item">Functional experience</li>
            <li className="policy-list-item">Category experience</li>
            <li className="policy-list-item">Job titles</li>
            <li className="policy-list-item">Grades</li>
            <li className="policy-list-item">Employment history (company names, start/end dates)</li>
            <li className="policy-list-item">Regions</li>
            <li className="policy-list-item">Organization types</li>
            <li className="policy-list-item">Skills / summary</li>
          </ul>

          <h3>C. Profile Photo</h3>
          <p>
            We only collect a profile photo that you upload from your device.
            Your device camera is not accessed.
          </p>
          <p>
            There is no resume, document, or PDF upload in the Platform.
          </p>

          <h3>D. Usage Data</h3>
          <ul className="policy-list">
            <li className="policy-list-item">Pages visited</li>
            <li className="policy-list-item">Search queries</li>
            <li className="policy-list-item">Clicks & interactions</li>
            <li className="policy-list-item">Dashboard activity</li>
            <li className="policy-list-item">Job interest actions</li>
          </ul>

          <h3>E. Device & Technical Data</h3>
          <ul className="policy-list">
            <li className="policy-list-item">IP address</li>
            <li className="policy-list-item">Browser type</li>
            <li className="policy-list-item">Device type</li>
            <li className="policy-list-item">Operating system</li>
            <li className="policy-list-item">Crash logs, diagnostics (via Firebase or similar)</li>
          </ul>
        </section>

        {/* 3. Mobile App Permissions */}
        <section className="policy-section">
          <h2>3. Mobile App Permissions</h2>
          <p>Our mobile apps request minimal permissions:</p>
          <ul className="policy-list">
            <li className="policy-list-item">Photos / Media Access – to upload a profile photo</li>
            <li className="policy-list-item">Internet access – required for the app to function</li>
            <li className="policy-list-item">Notifications (optional) – to send alerts and updates</li>
          </ul>
          <p>We do not access:</p>
          <ul className="policy-list">
            <li className="policy-list-item">Camera</li>
            <li className="policy-list-item">Documents</li>
            <li className="policy-list-item">Contacts</li>
            <li className="policy-list-item">Location</li>
            <li className="policy-list-item">Storage for file browsing</li>
          </ul>
        </section>

        {/* 4. Payments & Transactions */}
        <section className="policy-section">
          <h2>4. Payments & Transactions</h2>

          <h3>A) Stripe Payments (Website & Android App)</h3>
          <p>
            We use Stripe to process payments for premium features.
            Stripe may collect certain financial information directly from you, such as:
          </p>
          <ul className="policy-list">
            <li className="policy-list-item">Cardholder name</li>
            <li className="policy-list-item">Card details</li>
            <li className="policy-list-item">Billing address</li>
          </ul>
          <p>
            Stripe handles this data securely.
            We do not store or access your card details.
          </p>
          <p>
            You may review Stripe’s Privacy Policy at:
            <br />
            <a
              href="https://stripe.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://stripe.com/privacy
            </a>
          </p>

          <h3>B) Apple In-App Purchases (iOS App)</h3>
          <p>
            For iOS users, premium features are purchased through Apple In-App Purchase (IAP).
            Apple processes all payments, and we do not receive any credit card or Apple ID
            payment information.
          </p>
          <p>
            Please refer to Apple’s payment/privacy policies for more information.
          </p>
        </section>

        {/* 5. Third-Party Service Providers */}
        <section className="policy-section">
          <h2>5. Third-Party Service Providers</h2>
          <p>We may use trusted third-party providers for:</p>
          <ul className="policy-list">
            <li className="policy-list-item">Hosting</li>
            <li className="policy-list-item">Analytics (e.g., Firebase)</li>
            <li className="policy-list-item">Crash reporting</li>
            <li className="policy-list-item">Payment processing (Stripe / Apple IAP)</li>
            <li className="policy-list-item">Email notifications</li>
          </ul>
          <p>
            These providers may collect limited technical data required to perform their services.
            We do not sell your data.
          </p>
        </section>

        {/* 6. How We Use Your Information */}
        <section className="policy-section">
          <h2>6. How We Use Your Information</h2>
          <p>We use the information to:</p>
          <ul className="policy-list">
            <li className="policy-list-item">Create and manage your account</li>
            <li className="policy-list-item">Build your professional profile</li>
            <li className="policy-list-item">Provide analytics and personalized dashboards</li>
            <li className="policy-list-item">Handle payments through Stripe or Apple IAP</li>
            <li className="policy-list-item">Improve performance and security</li>
            <li className="policy-list-item">Send alerts, updates, and communications</li>
            <li className="policy-list-item">Comply with legal obligations</li>
          </ul>
        </section>

        {/* 7. Sharing of Information */}
        <section className="policy-section">
          <h2>7. Sharing of Information</h2>
          <p>We may share your information with:</p>
          <ul className="policy-list">
            <li className="policy-list-item">
              Employers/recruiters when you apply for a job
            </li>
            <li className="policy-list-item">
              Service providers (hosting, analytics, payments)
            </li>
            <li className="policy-list-item">
              Government authorities when legally required
            </li>
          </ul>
          <p>We do not share your information for advertising.</p>
        </section>

        {/* 8. Data Retention */}
        <section className="policy-section">
          <h2>8. Data Retention</h2>
          <p>
            We keep your data as long as your account is active.
            If you delete your account, we remove your information except:
          </p>
          <ul className="policy-list">
            <li className="policy-list-item">
              Minimal backups retained briefly for security
            </li>
            <li className="policy-list-item">
              Payment logs required by Stripe or Apple
            </li>
          </ul>
        </section>

        {/* 9. Your Rights */}
        <section className="policy-section">
          <h2>9. Your Rights</h2>
          <p>You may:</p>
          <ul className="policy-list">
            <li className="policy-list-item">Access or update your profile</li>
            <li className="policy-list-item">Delete your account</li>
            <li className="policy-list-item">Update email/notification preferences</li>
            <li className="policy-list-item">Request a copy of your data (where applicable)</li>
          </ul>
        </section>

        {/* 10. Security */}
        <section className="policy-section">
          <h2>10. Security</h2>
          <p>
            We use industry-standard security measures, including:
          </p>
          <ul className="policy-list">
            <li className="policy-list-item">HTTPS/SSL encryption</li>
            <li className="policy-list-item">Secure servers</li>
            <li className="policy-list-item">Access control</li>
            <li className="policy-list-item">
              Encrypted payment handling via Stripe and Apple
            </li>
          </ul>
          <p>
            However, no online system is 100% secure.
          </p>
        </section>

        {/* 11. Children’s Privacy */}
        <section className="policy-section">
          <h2>11. Children’s Privacy</h2>
          <p>
            The Platform is intended for adult professionals.
            We do not collect age or date of birth.
          </p>
          <p>
            We do not knowingly collect information from individuals under 18.
            If a minor has provided information, please contact us at
            <strong> support@goneet.com</strong>.
          </p>
        </section>

        {/* 12. Cookies */}
        <section className="policy-section">
          <h2>12. Cookies</h2>
          <p>
            Our website uses cookies to personalize content and analyze usage.
            You may disable cookies through your browser settings.
          </p>
        </section>

        {/* 13. Changes to This Policy */}
        <section className="policy-section">
          <h2>13. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy periodically.
            When updated, the “Last Updated” date will change.
          </p>
          <p>
            For significant updates, we may notify you via email or in-app messages.
          </p>
        </section>

        {/* 14. Contact Us */}
        <section className="policy-section">
          <h2>14. Contact Us</h2>
          <p>
            For questions about this Privacy Policy:
          </p>
          <p><strong>Email:</strong> support@goneet.com</p>
        </section>
      </div>
    </Layout>
  );
};

export default PrivacyPolicyPage;
